// Onda ao vivo: desenha barras verticais a partir do AnalyserNode REAL do microfone
// (getByteFrequencyData), rolando à esquerda. É prova de captação — quando o mic não
// capta som, as barras achatam. Roda em <canvas> com requestAnimationFrame, fora do
// state do React, para 60fps liso sem re-render. Cores e proporções (3px de barra,
// 3px de gap, 140px de altura) vêm de mockups/1c-celular-gravando.html.

import { useEffect, useRef, type MutableRefObject } from 'react';

import { corC } from '../../styles/tokensCelular';

interface Props {
  analyser: MutableRefObject<AnalyserNode | null>;
  ativo: boolean; // false quando pausado: a onda "escorre" para o silêncio
}

const LARG_BARRA = 3;
const GAP = 3;

export function Onda({ analyser, ativo }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const barrasRef = useRef<number[]>([]);
  const ativoRef = useRef(ativo);
  ativoRef.current = ativo;

  useEffect(() => {
    if (!canvasRef.current) return;

    let raf = 0;
    function frame(): void {
      raf = requestAnimationFrame(frame);
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const nBarras = Math.max(1, Math.floor(w / (LARG_BARRA + GAP)));
      const an = analyser.current;
      if (an && ativoRef.current) {
        const buf = new Uint8Array(an.frequencyBinCount);
        an.getByteFrequencyData(buf);
        let soma = 0;
        for (let i = 0; i < buf.length; i++) soma += buf[i]!;
        const amp = soma / buf.length / 255; // 0..1
        barrasRef.current.push(Math.min(1, amp * 1.8));
      } else {
        barrasRef.current.push(0);
      }
      while (barrasRef.current.length > nBarras) barrasRef.current.shift();

      ctx.clearRect(0, 0, w, h);
      const barras = barrasRef.current;
      for (let i = 0; i < barras.length; i++) {
        const alt = Math.max(2, barras[i]! * h);
        const x = i * (LARG_BARRA + GAP);
        const y = (h - alt) / 2;
        ctx.fillStyle = i >= barras.length - 2 ? corC.ondaRecente : corC.ondaBase;
        ctx.fillRect(x, y, LARG_BARRA, alt);
      }
    }
    frame();
    return () => cancelAnimationFrame(raf);
  }, [analyser]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: 140, display: 'block' }} />;
}
